import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';
import { Environment } from '../../../environment';

export type TStatusSessaoCaixa = 'aberta' | 'fechada' | 'cancelada';

export interface ISessaoCaixaAtiva {
  idsessao: string;
  numero: string;
  iduser: string;
  idconta: string;
  nomeConta: string;
  dataabertura: string;
  status: TStatusSessaoCaixa;
  valorabertura?: number;
  vendasNoTurno?: number;
  nomeUsuario?: string;
}

export interface IFormaPagamentoAbertura {
  valor: number;
  categoria: string;
}

export interface IFormaPagamentoFechamento {
  valor: number;
  categoria: string;
}

export interface ISaldoPreviewCaixa {
  saldoanterior: number;
  saldototal: number;
}

export interface IPorFormaPagamento {
  categoria: string;
  valor: number;
}

export interface IListagemSessaoCaixa {
  idsessao: string;
  numero: string;
  iduser: string;
  nomeUsuario: string;
  idconta: string;
  nomeConta: string;
  dataabertura: string;
  datafechamento?: string;
  status: TStatusSessaoCaixa;
  valorabertura?: number;
}

type TComTotalCount = {
  data: IListagemSessaoCaixa[];
  totalCount: number;
};

interface ErrorResponse {
  errors?: { default: string };
}

const extractError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    const message = axiosError.response?.data?.errors?.default;
    if (message) return new Error(message);
    if (axiosError.request) return new Error('Nenhuma resposta do servidor');
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

// Sessão de caixa do usuário logado, achável de qualquer computador — nunca
// lida do localStorage, sempre desta chamada.
const getAtiva = async (): Promise<ISessaoCaixaAtiva | null | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/ativa');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

const abrir = async (
  idconta: string,
  formasPagamento: IFormaPagamentoAbertura[],
): Promise<ISessaoCaixaAtiva | Error> => {
  try {
    const { data } = await Api.post('/sessaocaixa/abrir', { idconta, formasPagamento });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Conta de destino não é escolhida aqui — o backend sempre usa o Cofre
// (mesma conta da abertura, ver fecharSessao no backend).
const fechar = async (
  formasPagamento: IFormaPagamentoFechamento[],
): Promise<{ idconta: string } | Error> => {
  try {
    const { data } = await Api.post('/sessaocaixa/fechar', { formasPagamento });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Saldo real (todo o histórico, não só o turno atual) por forma de
// pagamento do caixa da sessão ativa — usado pra mostrar o valor total de
// Dinheiro disponível antes de fechar (ver DialogFecharSessaoCaixa). Inclui
// dinheiro que ficou de um turno anterior nunca fechado formalmente
// (virada de dia sem fechar, ver buscarSessaoAtiva no backend).
const getSaldoRealSessao = async (): Promise<IPorFormaPagamento[] | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/saldoreal');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

export interface IFluxoSessaoResumo {
  saldoanterior: number;
  credito: number;
  debito: number;
  saldo: number;
  saldoreal: number;
  porFormaPagamento: IPorFormaPagamento[];
}

// Movimento só desta sessão (vendas em qualquer forma, sangria, suprimento,
// transferência) desde a abertura — diferente de getSaldoRealSessao (all-time,
// usado só pra validar o valor a transferir). `idconta`/`dataabertura` só são
// usados pelo backend nos tenants sem sessão (cai pro equivalente por turno);
// nos tenants com sessão, o resultado já vem escopado pelo idsessao do
// usuário, sem precisar desses parâmetros.
const getFluxoSessao = async (idconta: string, dataabertura: string): Promise<IFluxoSessaoResumo | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/fluxo', {
      params: { idconta, datainicial: dataabertura, datafinal: new Date().toISOString() },
    });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Preview do saldo real do caixa (sem escopo de turno) antes de abrir a
// sessão nele — ainda não existe sessão, então idconta vem explícito.
const getSaldoPreview = async (idconta: string): Promise<ISaldoPreviewCaixa | Error> => {
  try {
    const { data } = await Api.get('/sessaocaixa/saldo', { params: { idconta } });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Listagem de sessões (abertas, fechadas e canceladas) — busca por número
// (derivado do idsessao, ver gerarNumeroSessao no backend).
const getAll = async (page = 1, filter = '', limit = Environment.LIMITE_DE_LINHAS): Promise<TComTotalCount | Error> => {
  try {
    const { data, headers } = await Api.get('/sessaocaixa', { params: { page, filter, limit } });
    return {
      data,
      totalCount: Number(headers['x-total-count'] || limit),
    };
  } catch (error) {
    return extractError(error);
  }
};

export const SessaoCaixaService = {
  getAtiva,
  abrir,
  fechar,
  getSaldoRealSessao,
  getFluxoSessao,
  getSaldoPreview,
  getAll,
};
